import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { IconPencil, IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, call, type Food, type Schemas } from "../api/client";
import { grams, kcal } from "../lib/format";

const CONFIDENCE_COLORS = { high: "teal", medium: "yellow", low: "orange" } as const;

export function FoodsPage() {
  const [q, setQ] = useState("");
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [debounced] = useDebouncedValue(q, 250);
  const [editing, setEditing] = useState<Food | "new" | null>(null);
  const queryClient = useQueryClient();
  const foods = useQuery({
    queryKey: ["foods", "list", scope, debounced],
    queryFn: () =>
      call(api.GET("/api/v1/foods", { params: { query: { q: debounced, scope, limit: 100 } } })),
  });
  const archive = useMutation({
    mutationFn: (id: number) =>
      call(api.DELETE("/api/v1/foods/{food_id}", { params: { path: { food_id: id } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["foods"] }),
    meta: { errorTitle: "Couldn't remove the food" },
  });

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Foods</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing("new")}>
          New food
        </Button>
      </Group>
      <Text size="sm" c="dimmed">
        Foods you or your agents saved. Logged entries keep their own numbers, so fixing a food
        here only affects future logs.
      </Text>
      <Group grow>
        <TextInput placeholder="Search" value={q} onChange={(e) => setQ(e.currentTarget.value)} />
        <SegmentedControl
          value={scope}
          onChange={(v) => setScope(v as "mine" | "all")}
          data={[
            { value: "mine", label: "Mine" },
            { value: "all", label: "Mine + catalog" },
          ]}
        />
      </Group>
      {foods.isPending && <Loader />}
      {foods.data?.length === 0 && <Text c="dimmed">No foods yet.</Text>}
      {foods.data?.map((f) => (
        <Card key={f.id} withBorder padding="sm">
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Group gap="xs">
                <Text fw={600}>{f.name}</Text>
                {f.brand && <Text c="dimmed" size="sm">{f.brand}</Text>}
                {f.is_catalog && <Badge size="xs" color="gray" variant="light">catalog</Badge>}
                {f.confidence && (
                  <Badge size="xs" variant="light" color={CONFIDENCE_COLORS[f.confidence]}>
                    {f.confidence} confidence
                  </Badge>
                )}
              </Group>
              <Text size="sm">
                <b>{kcal(f.kcal)} kcal</b> per {f.serving_name}
                {f.serving_grams ? ` (${f.serving_grams} g)` : ""} · P {grams(f.protein_g)} · C{" "}
                {grams(f.carbs_g)} · F {grams(f.fat_g)}
              </Text>
              {f.source_url && (
                <Anchor href={f.source_url} target="_blank" rel="noreferrer" size="xs" truncate>
                  Source{f.source_checked_on ? `, checked ${f.source_checked_on}` : ""}
                </Anchor>
              )}
            </Stack>
            {!f.is_catalog && (
              <Group gap={4} wrap="nowrap">
                <ActionIcon variant="subtle" onClick={() => setEditing(f)} aria-label={`Edit ${f.name}`}>
                  <IconPencil size={16} />
                </ActionIcon>
                <ActionIcon variant="subtle" color="red" onClick={() => archive.mutate(f.id)} aria-label={`Remove ${f.name}`}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            )}
          </Group>
        </Card>
      ))}
      <FoodModal food={editing} onClose={() => setEditing(null)} />
    </Stack>
  );
}

type Form = {
  name: string;
  brand: string;
  serving_name: string;
  serving_grams: number | string;
  kcal: number | string;
  protein_g: number | string;
  carbs_g: number | string;
  fat_g: number | string;
  source_url: string;
  confidence: string | null;
  notes: string;
  servings: { name: string; multiplier: number | string }[];
};

function toForm(f: Food | null): Form {
  return {
    name: f?.name ?? "",
    brand: f?.brand ?? "",
    serving_name: f?.serving_name ?? "1 serving",
    serving_grams: f?.serving_grams ?? "",
    kcal: f?.kcal ?? "",
    protein_g: f?.protein_g ?? "",
    carbs_g: f?.carbs_g ?? "",
    fat_g: f?.fat_g ?? "",
    source_url: f?.source_url ?? "",
    confidence: f?.confidence ?? null,
    notes: f?.notes ?? "",
    servings: f?.servings.map((s) => ({ name: s.name, multiplier: s.multiplier })) ?? [],
  };
}

function FoodModal({ food, onClose }: { food: Food | "new" | null; onClose: () => void }) {
  const phone = useMediaQuery("(max-width: 36em)");
  return (
    <Modal opened={food !== null} onClose={onClose} title={food === "new" ? "New food" : "Edit food"} size="lg" fullScreen={phone}>
      {food !== null && <FoodForm food={food === "new" ? null : food} onDone={onClose} key={food === "new" ? "new" : food.id} />}
    </Modal>
  );
}

function FoodForm({ food, onDone }: { food: Food | null; onDone: () => void }) {
  const [form, setForm] = useState<Form>(() => toForm(food));
  const queryClient = useQueryClient();
  const num = (v: number | string) => (v === "" ? null : Number(v));
  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm({ ...form, [key]: value });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        brand: form.brand.trim() || null,
        serving_name: form.serving_name.trim() || "1 serving",
        serving_grams: num(form.serving_grams),
        kcal: Number(form.kcal),
        protein_g: num(form.protein_g),
        carbs_g: num(form.carbs_g),
        fat_g: num(form.fat_g),
        source_url: form.source_url.trim() || null,
        confidence: form.confidence as Schemas["Confidence"] | null,
        notes: form.notes.trim() || null,
        servings: form.servings
          .filter((s) => s.name.trim() && Number(s.multiplier) > 0)
          .map((s) => ({ name: s.name.trim(), multiplier: Number(s.multiplier) })),
      };
      return food
        ? call(api.PATCH("/api/v1/foods/{food_id}", { params: { path: { food_id: food.id } }, body }))
        : call(api.POST("/api/v1/foods", { body }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["foods"] });
      onDone();
    },
  });
  const valid = form.name.trim() !== "" && form.kcal !== "";

  return (
    <Stack>
      <Group grow>
        <TextInput label="Name" value={form.name} onChange={(e) => set("name", e.currentTarget.value)} required data-autofocus />
        <TextInput label="Brand" value={form.brand} onChange={(e) => set("brand", e.currentTarget.value)} />
      </Group>
      <Group grow>
        <TextInput label="Reference serving" description="Nutrients below are for this" value={form.serving_name} onChange={(e) => set("serving_name", e.currentTarget.value)} />
        <NumberInput label="Serving weight (g)" description="Optional; allows logging by grams" min={0} value={form.serving_grams} onChange={(v) => set("serving_grams", v)} hideControls />
      </Group>
      <Group grow>
        <NumberInput label="kcal" min={0} value={form.kcal} onChange={(v) => set("kcal", v)} required hideControls />
        <NumberInput label="Protein (g)" min={0} value={form.protein_g} onChange={(v) => set("protein_g", v)} hideControls />
      </Group>
      <Group grow>
        <NumberInput label="Carbs (g)" min={0} value={form.carbs_g} onChange={(v) => set("carbs_g", v)} hideControls />
        <NumberInput label="Fat (g)" min={0} value={form.fat_g} onChange={(v) => set("fat_g", v)} hideControls />
      </Group>
      <Stack gap={6}>
        <Text size="sm" fw={500}>
          Other serving sizes
        </Text>
        {form.servings.map((s, i) => (
          <Group key={i} wrap="nowrap" align="flex-end">
            <TextInput placeholder="e.g. half" value={s.name} onChange={(e) => set("servings", form.servings.map((x, j) => (j === i ? { ...x, name: e.currentTarget.value } : x)))} style={{ flex: 2 }} aria-label="Serving name" />
            <NumberInput placeholder="× reference" min={0} value={s.multiplier} onChange={(v) => set("servings", form.servings.map((x, j) => (j === i ? { ...x, multiplier: v } : x)))} style={{ flex: 1 }} hideControls aria-label="Multiplier" />
            <ActionIcon variant="subtle" color="gray" onClick={() => set("servings", form.servings.filter((_, j) => j !== i))} aria-label="Remove serving">
              <IconX size={16} />
            </ActionIcon>
          </Group>
        ))}
        <Button variant="subtle" size="xs" w="fit-content" leftSection={<IconPlus size={14} />} onClick={() => set("servings", [...form.servings, { name: "", multiplier: 1 }])}>
          Add serving size
        </Button>
      </Stack>
      <Group grow>
        <TextInput label="Source URL" value={form.source_url} onChange={(e) => set("source_url", e.currentTarget.value)} />
        <Select label="Confidence" data={["high", "medium", "low"]} value={form.confidence} onChange={(v) => set("confidence", v)} clearable />
      </Group>
      <Textarea label="Notes" value={form.notes} onChange={(e) => set("notes", e.currentTarget.value)} autosize minRows={1} />
      <Group justify="flex-end">
        <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}
